import React, { useEffect, useState, useCallback, useMemo, useContext, createContext } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StoreApiCtx = createContext(null);

export function StoreProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("furmigo_cart") || "[]"); } catch { return []; }
  });
  const [customer, setCustomer] = useState(() => {
    try { return JSON.parse(localStorage.getItem("furmigo_store_customer") || "null"); } catch { return null; }
  });

  useEffect(() => { localStorage.setItem("furmigo_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("furmigo_store_customer", JSON.stringify(customer)); }, [customer]);

  const addToCart = useCallback((product, qty = 1) => {
    setCart(prev => {
      const existing = prev.find(i => i.product_id === product.id);
      if (existing) return prev.map(i => i.product_id === product.id ? { ...i, qty: i.qty + qty } : i);
      return [...prev, { product_id: product.id, name: product.name, image_path: product.image_path, sale_price: product.sale_price, qty }];
    });
  }, []);
  const updateQty = useCallback((product_id, qty) => {
    setCart(prev => qty <= 0 ? prev.filter(i => i.product_id !== product_id) : prev.map(i => i.product_id === product_id ? { ...i, qty } : i));
  }, []);
  const removeFromCart = useCallback((product_id) => setCart(prev => prev.filter(i => i.product_id !== product_id)), []);
  const clearCart = useCallback(() => setCart([]), []);
  const itemCount = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);
  const cartTotal = useMemo(() => cart.reduce((s, i) => s + i.qty * (i.sale_price || 0), 0), [cart]);

  // OTP login flow
  const requestOtp = useCallback(async (mobile) => {
    const { data } = await axios.post(`${API}/otp/request`, { mobile });
    return data;
  }, []);
  const verifyOtp = useCallback(async (mobile, code, name = null) => {
    const { data } = await axios.post(`${API}/otp/verify`, { mobile, code, name });
    setCustomer({
      id: data.customer_id,
      name: data.name,
      mobile,
      token: data.token,
      wallet_balance: data.wallet_balance || 0,
      verified: true,
    });
    return data;
  }, []);

  const registerCustomer = useCallback(async (mobile, token, name) => {
    const { data } = await axios.post(`${API}/store/customer/register`, null,
      { params: { mobile, token, name } });
    setCustomer(prev => ({ ...(prev || {}), id: data.customer_id, name: data.name }));
    return data;
  }, []);

  const fetchMe = useCallback(async () => {
    if (!customer?.token || !customer?.mobile) return null;
    try {
      const { data } = await axios.get(`${API}/store/customer/me`, {
        params: { mobile: customer.mobile, token: customer.token },
      });
      setCustomer(prev => ({
        ...prev,
        id: data.customer?.id || prev.id,
        name: data.customer?.name || prev.name,
        email: data.customer?.email,
        address: data.customer?.address,
        wallet_balance: data.wallet_balance,
      }));
      return data;
    } catch {
      // 401 → token expired, force logout
      setCustomer(null);
      return null;
    }
  }, [customer?.token, customer?.mobile]);

  const logout = useCallback(() => setCustomer(null), []);

  const value = { cart, addToCart, updateQty, removeFromCart, clearCart, itemCount, cartTotal, customer, requestOtp, verifyOtp, registerCustomer, fetchMe, logout, API };
  return <StoreApiCtx.Provider value={value}>{children}</StoreApiCtx.Provider>;
}

export function useStore() {
  const ctx = useContext(StoreApiCtx);
  if (!ctx) throw new Error("useStore must be used inside <StoreProvider>");
  return ctx;
}

export const storeApi = {
  products: (params) => axios.get(`${API}/store/products`, { params }).then(r => r.data),
  product: (pid) => axios.get(`${API}/store/products/${pid}`).then(r => r.data),
  categories: () => axios.get(`${API}/store/categories`).then(r => r.data),
  brands: () => axios.get(`${API}/store/brands`).then(r => r.data),
  banners: (position) => axios.get(`${API}/store/banners`, { params: { position } }).then(r => r.data),
  branches: () => axios.get(`${API}/store/branches`).then(r => r.data),
  checkout: (payload) => axios.post(`${API}/store/checkout`, payload).then(r => r.data),
  order: (order_no, mobile) => axios.get(`${API}/store/orders/${order_no}`, { params: { mobile } }).then(r => r.data),
};
